/**
 * BrandingShell — Full-page wallpaper + readability overlay + centered content
 * 
 * Replicates YulOnTap's BrandingShell.jsx architecture.
 * Wraps guest-facing survey/alert pages with:
 *   1. Full-viewport wallpaper background
 *   2. 50% dark readability overlay
 *   3. Centered content area
 *
 * When no wallpaper is present, falls back to a gradient background.
 */
import React from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function resolveUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
}

export default function BrandingShell({
  wallpaperUrl,
  fallbackGradient = 'from-green-600 to-green-800',
  children,
}) {
  const resolved = resolveUrl(wallpaperUrl);
  const hasWallpaper = !!resolved;

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Layer 1: Background — wallpaper or gradient */}
      {hasWallpaper ? (
        <div
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: `url(${resolved})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      ) : (
        <div className={`fixed inset-0 z-0 bg-gradient-to-br ${fallbackGradient}`} />
      )}

      {/* Layer 2: Readability overlay (only when wallpaper exists) */}
      {hasWallpaper && (
        <div
          className="fixed inset-0 z-[1] pointer-events-none"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        />
      )}

      {/* Layer 3: Content — sits above overlay */}
      <div className="relative z-[2] w-full max-w-md">
        {children}
      </div>
    </div>
  );
}
